import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart, LineChart, CartesianGrid, Area } from 'recharts';
import { CText, CLoader } from '../../components/common';
import { getPriceHistory, PricePoint } from '../../api/copilot';

const CHART_COLORS = ['#6B39F4', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#6366f1', '#f97316'];

// ---------- Segment Charts (Revenue Mix) ----------

interface SegmentChartsProps {
  productSegments?: Array<Record<string, string>>;
  geographicSegments?: Array<Record<string, string>>;
  colors: any;
}

function getLatestSegmentData(segments: Array<Record<string, string>>): { name: string; value: number; period: string }[] {
  if (!segments || segments.length === 0) return [];

  const keys = Object.keys(segments[0]);
  const labelKey = keys[0]; // First column is the segment label
  const dataKeys = keys.slice(1);
  if (dataKeys.length === 0) return [];

  // Use the last data column (most recent period on screener-style tables)
  const latestKey = dataKeys[dataKeys.length - 1];

  return segments
    .map(row => {
      const rawValue = row[latestKey] || '0';
      const numVal = parseFloat(String(rawValue).replace(/,/g, '').replace(/%/g, ''));
      return {
        name: row[labelKey] || 'Unknown',
        value: isNaN(numVal) ? 0 : Math.abs(numVal),
        period: latestKey,
      };
    })
    .filter(d => d.value > 0);
}

const SegmentPieChart: React.FC<{
  data: { name: string; value: number }[];
  title: string;
  period: string;
  colors: any;
}> = ({ data, title, period, colors }) => {
  if (data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <CText type="M14" color={colors.primary}>{title}</CText>
        <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: colors.primary + '15', color: colors.primary }}>
          {period}
        </span>
      </div>
      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="w-full md:w-1/2" style={{ height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={85}
                innerRadius={45}
                dataKey="value"
                stroke="none"
                paddingAngle={2}
              >
                {data.map((_, idx) => (
                  <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [`₹${value.toLocaleString()} Cr`, '']}
                contentStyle={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-full md:w-1/2 space-y-2">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                <CText type="r12" color={colors.textColor} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.name}
                </CText>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CText type="r12" color={colors.grayScale500}>₹{d.value.toLocaleString()} Cr</CText>
                <CText type="M12" color={colors.textColor}>
                  {total > 0 ? `${((d.value / total) * 100).toFixed(1)}%` : '-'}
                </CText>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SegmentCharts: React.FC<SegmentChartsProps> = ({ productSegments, geographicSegments, colors }) => {
  const productData = getLatestSegmentData(productSegments || []);
  const geoData = getLatestSegmentData(geographicSegments || []);

  if (productData.length === 0 && geoData.length === 0) {
    return (
      <div className="text-center py-8">
        <CText type="M14" color={colors.grayScale500}>No segment data available</CText>
      </div>
    );
  }

  return (
    <div>
      {productData.length > 0 && (
        <SegmentPieChart data={productData} title="Revenue by Segment" period={productData[0]?.period || ''} colors={colors} />
      )}
      {geoData.length > 0 && (
        <SegmentPieChart data={geoData} title="Revenue by Geography" period={geoData[0]?.period || ''} colors={colors} />
      )}
    </div>
  );
};

// ---------- Shareholding Chart ----------

interface ShareholdingChartProps {
  shareholdingRatios?: Array<Record<string, string>>;
  colors: any;
}

const NON_PERCENTAGE_LABELS = ['no. of shareholders', 'no. of shareholder', 'number of shareholders'];
function isPercentageHolder(label: string): boolean {
  return !NON_PERCENTAGE_LABELS.some(x => label.toLowerCase().includes(x));
}

export const ShareholdingChart: React.FC<ShareholdingChartProps> = ({ shareholdingRatios, colors }) => {
  if (!shareholdingRatios || shareholdingRatios.length === 0) {
    return (
      <div className="text-center py-8">
        <CText type="M14" color={colors.grayScale500}>No shareholding data available</CText>
      </div>
    );
  }

  const keys = Object.keys(shareholdingRatios[0]);
  const labelKey = keys[0];
  const periodKeys = keys.slice(1);
  const percentageRows = shareholdingRatios.filter(row => isPercentageHolder(row[labelKey] || ''));

  // Build chart data: each period is one bar group, stacked by holder type
  const chartData = periodKeys.map(period => {
    const entry: Record<string, any> = { period };
    percentageRows.forEach(row => {
      const name = row[labelKey] || 'Unknown';
      const rawVal = row[period];
      const numVal = parseFloat(String(rawVal || '0').replace(/,/g, '').replace(/%/g, ''));
      entry[name] = isNaN(numVal) ? 0 : numVal;
    });
    return entry;
  });

  const holderTypes = percentageRows.map(row => row[labelKey] || 'Unknown');

  if (chartData.length === 0) return null;

  // Also show a summary donut for latest period
  const latestPeriod = periodKeys[periodKeys.length - 1];
  const latestData = percentageRows
    .map(row => ({
      name: row[labelKey] || 'Unknown',
      value: parseFloat(String(row[latestPeriod] || '0').replace(/,/g, '').replace(/%/g, '')) || 0,
    }))
    .filter(d => d.value > 0);

  return (
    <div>
      <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Shareholding Pattern</CText>

      {/* Summary donut */}
      {latestData.length > 0 && (
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <div className="w-full md:w-1/2" style={{ height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={latestData} cx="50%" cy="50%" outerRadius={80} innerRadius={40} dataKey="value" stroke="none" paddingAngle={2}>
                  {latestData.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value.toFixed(1)}%`, '']}
                  contentStyle={{
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full md:w-1/2 space-y-2">
            {latestData.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <CText type="r12" color={colors.textColor}>{d.name}</CText>
                </div>
                <CText type="M12" color={colors.textColor}>{d.value.toFixed(1)}%</CText>
              </div>
            ))}
            <CText type="r10" color={colors.grayScale400} style={{ marginTop: 4 }}>as of {latestPeriod}</CText>
          </div>
        </div>
      )}

      {/* Trend bar chart */}
      {chartData.length > 1 && (
        <>
          <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 8 }}>Trend over time</CText>
          <div style={{ height: 280, width: '100%' }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <XAxis dataKey="period" tick={{ fill: colors.grayScale500, fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fill: colors.grayScale500, fontSize: 11 }} unit="%" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [`${value.toFixed(1)}%`, '']}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {holderTypes.map((type, i) => (
                  <Bar key={type} dataKey={type} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Growth Metrics ----------

interface GrowthMetricsProps {
  salesGrowth?: Record<string, string>;
  profitGrowth?: Record<string, string>;
  stockPriceCagr?: Record<string, string>;
  roeGrowth?: Record<string, string>;
  yearlyResults?: Array<Record<string, any>>;
  colors: any;
}

export const GrowthMetrics: React.FC<GrowthMetricsProps> = ({
  salesGrowth, profitGrowth, stockPriceCagr, roeGrowth, yearlyResults, colors,
}) => {
  const metrics = [
    { label: 'Sales Growth', data: salesGrowth },
    { label: 'Profit Growth', data: profitGrowth },
    { label: 'Stock Price CAGR', data: stockPriceCagr },
    { label: 'ROE', data: roeGrowth },
  ].filter(m => m.data && Object.keys(m.data).length > 0);

  // Compute OPM trend from yearly results
  const opmTrend = useMemo(() => {
    if (!yearlyResults || yearlyResults.length === 0) return [];
    const labelKey = Object.keys(yearlyResults[0])[0];
    const salesRow = yearlyResults.find(r => String(r[labelKey] || '').toLowerCase().trim() === 'sales');
    const opRow = yearlyResults.find(r => {
      const name = String(r[labelKey] || '').toLowerCase().trim();
      return name === 'operating profit' || name === 'opm';
    });
    if (!salesRow || !opRow) return [];
    const periods = Object.keys(salesRow).filter(k => k !== labelKey && k !== 'TTM');
    return periods
      .map(p => {
        const s = parseFloat(String(salesRow[p] || '0').replace(/,/g, ''));
        const o = parseFloat(String(opRow[p] || '0').replace(/,/g, ''));
        if (isNaN(s) || isNaN(o) || s <= 0) return null;
        return { period: p, opm: parseFloat(((o / s) * 100).toFixed(1)) };
      })
      .filter((d): d is { period: string; opm: number } => d !== null);
  }, [yearlyResults]);

  if (metrics.length === 0 && opmTrend.length === 0) return null;

  // Get all unique period keys
  const allPeriods = Array.from(
    new Set(metrics.flatMap(m => Object.keys(m.data!)))
  ).sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  const getValueColor = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return colors.textColor;
    if (num > 0) return '#22c55e';
    if (num < 0) return '#ef4444';
    return colors.textColor;
  };

  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
    >
      <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Growth (CAGR)</CText>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left px-2 py-1">
                <CText type="r12" color={colors.grayScale400}>Metric</CText>
              </th>
              {allPeriods.map(p => (
                <th key={p} className="text-right px-2 py-1">
                  <CText type="r12" color={colors.grayScale400}>{p}</CText>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => (
              <tr key={m.label}>
                <td className="px-2 py-1.5">
                  <CText type="M12" color={colors.textColor}>{m.label}</CText>
                </td>
                {allPeriods.map(p => {
                  const val = m.data![p];
                  return (
                    <td key={p} className="text-right px-2 py-1.5">
                      <CText type="M12" color={val ? getValueColor(val) : colors.grayScale400}>
                        {val ? `${val}%` : '-'}
                      </CText>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* OPM Trend Chart */}
      {opmTrend.length > 1 && (
        <div className="mt-4">
          <CText type="M12" color={colors.grayScale500} style={{ marginBottom: 8, display: 'block' }}>
            Operating Profit Margin Trend
          </CText>
          <div style={{ height: 200, width: '100%' }}>
            <ResponsiveContainer>
              <LineChart data={opmTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 10, fill: colors.grayScale400 }}
                  angle={-30}
                  textAnchor="end"
                  height={45}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: colors.grayScale400 }}
                  width={40}
                  unit="%"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [`${value.toFixed(1)}%`, 'OPM']}
                />
                <Line
                  type="monotone"
                  dataKey="opm"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ fill: '#6366f1', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Ratio Toggle Chart ----------

const RATIO_OPTIONS = [
  { key: 'EPS', barMatches: ['eps in rs', 'eps', 'earnings per share'], barLabel: 'EPS (Rs)', unit: '' },
  { key: 'Sales', barMatches: ['sales'], barLabel: 'Sales (Cr)', unit: 'Cr' },
  { key: 'EBITDA', barMatches: ['operating profit'], barLabel: 'EBITDA (Cr)', unit: 'Cr' },
  { key: 'Net Profit', barMatches: ['net profit'], barLabel: 'Net Profit (Cr)', unit: 'Cr' },
] as const;

interface RatioChartProps {
  ratios?: Array<Record<string, string>>;
  yearlyResults?: Array<Record<string, any>>;
  balanceSheets?: Array<Record<string, any>>;
  currentRatios?: { pe?: number; pb?: number; mcapSales?: number; evEbitda?: number };
  colors: any;
}

function findMetricRow(
  barMatches: readonly string[],
  ...tables: (Array<Record<string, any>> | undefined)[]
): Record<string, any> | undefined {
  for (const table of tables) {
    if (!table || table.length === 0) continue;
    const labelKey = Object.keys(table[0])[0];
    const found = table.find(row => {
      const name = String(row[labelKey] || '').toLowerCase();
      return barMatches.some(m => name.includes(m));
    });
    if (found) return found;
  }
  return undefined;
}

function extractTimeSeries(row: Record<string, any>): { period: string; value: number }[] {
  const labelKey = Object.keys(row)[0];
  const data: { period: string; value: number }[] = [];
  for (const [key, raw] of Object.entries(row)) {
    if (key === labelKey || key === 'TTM') continue;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
    if (!isNaN(num)) data.push({ period: key, value: num });
  }
  return data;
}

export const RatioChart: React.FC<RatioChartProps> = ({ yearlyResults, balanceSheets, currentRatios, colors }) => {
  const [activeMetric, setActiveMetric] = useState<typeof RATIO_OPTIONS[number]['key']>(RATIO_OPTIONS[0].key);

  const activeOption = RATIO_OPTIONS.find(o => o.key === activeMetric)!;
  const metricRow = findMetricRow(activeOption.barMatches, yearlyResults, balanceSheets);

  const chartData = metricRow ? extractTimeSeries(metricRow) : [];

  // No data at all across any metric
  const hasAnyData = RATIO_OPTIONS.some(opt =>
    !!findMetricRow(opt.barMatches, yearlyResults, balanceSheets)
  );
  if (!hasAnyData) return null;

  // Current ratio badges
  const ratioBadges = currentRatios ? [
    { label: 'P/E', value: currentRatios.pe },
    { label: 'P/B', value: currentRatios.pb },
    { label: 'MCap/Sales', value: currentRatios.mcapSales },
    { label: 'EV/EBITDA', value: currentRatios.evEbitda },
  ].filter(b => b.value != null && !isNaN(b.value!)) : [];

  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
    >
      <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Valuation & Key Metrics</CText>

      {/* Current ratio badges */}
      {ratioBadges.length > 0 && (
        <div className="flex gap-3 mb-4 flex-wrap">
          {ratioBadges.map(b => (
            <div
              key={b.label}
              className="px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: colors.primary + '12', border: `1px solid ${colors.primary}30` }}
            >
              <CText type="r10" color={colors.grayScale500}>{b.label}</CText>
              <CText type="M14" color={colors.primary} style={{ display: 'block' }}>
                {b.value!.toFixed(1)}
              </CText>
            </div>
          ))}
        </div>
      )}

      {/* Toggle buttons */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {RATIO_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setActiveMetric(opt.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{
              backgroundColor: activeMetric === opt.key ? colors.primary : colors.backgroundColor,
              color: activeMetric === opt.key ? colors.white : colors.grayScale500,
              border: `1px solid ${activeMetric === opt.key ? colors.primary : colors.borderColor}`,
            }}
          >
            {opt.key}
          </button>
        ))}
      </div>

      {/* Bar chart for the selected metric */}
      {chartData.length > 0 ? (
        <div style={{ height: 260, width: '100%' }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <XAxis
                dataKey="period"
                tick={{ fill: colors.grayScale500, fontSize: 10 }}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tick={{ fill: colors.grayScale400, fontSize: 10 }}
                width={60}
                tickFormatter={(v: number) =>
                  Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()
                }
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => [
                  v.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                  activeOption.barLabel,
                ]}
              />
              <Bar
                dataKey="value"
                fill={colors.primary + '30'}
                stroke={colors.primary}
                strokeWidth={1}
                radius={[4, 4, 0, 0]}
                name={activeOption.barLabel}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={colors.primary}
                strokeWidth={2}
                dot={{ fill: colors.primary, r: 3 }}
                activeDot={{ r: 5 }}
                name="Trend"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-6">
          <CText type="r12" color={colors.grayScale400}>No data available for {activeMetric}</CText>
        </div>
      )}
    </div>
  );
};

// ---------- Historical Valuation Ratios Line Chart ----------

const RATIO_LINE_OPTIONS = [
  { key: 'pe' as const, label: 'P/E', color: '#6366f1' },
  { key: 'pb' as const, label: 'P/B', color: '#22c55e' },
  { key: 'mcapSales' as const, label: 'MCap/Sales', color: '#f59e0b' },
  { key: 'evEbitda' as const, label: 'EV/EBITDA', color: '#ef4444' },
];

interface HistoricalRatiosChartProps {
  data: Array<{ date: string; pe?: number; pb?: number; mcapSales?: number; evEbitda?: number }>;
  colors: any;
}

export const HistoricalRatiosChart: React.FC<HistoricalRatiosChartProps> = ({ data, colors }) => {
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set(['pe']));

  if (!data || data.length === 0) return null;

  const toggleKey = (key: string) => {
    setActiveKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  };

  const chartData = data.map(p => ({ ...p, label: formatLabel(p.date) }));

  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
    >
      <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Historical Valuation Ratios</CText>

      {/* Toggle buttons */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {RATIO_LINE_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => toggleKey(opt.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{
              backgroundColor: activeKeys.has(opt.key) ? opt.color : colors.backgroundColor,
              color: activeKeys.has(opt.key) ? '#fff' : colors.grayScale500,
              border: `1px solid ${activeKeys.has(opt.key) ? opt.color : colors.borderColor}`,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Line chart */}
      <div style={{ height: 280, width: '100%' }}>
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: colors.grayScale400 }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: colors.grayScale400 }}
              width={45}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value: number, name: string) => {
                const opt = RATIO_LINE_OPTIONS.find(o => o.key === name);
                return [value.toFixed(2), opt?.label || name];
              }}
              labelFormatter={(label) => label}
            />
            {RATIO_LINE_OPTIONS.filter(o => activeKeys.has(o.key)).map(opt => (
              <Line
                key={opt.key}
                type="monotone"
                dataKey={opt.key}
                stroke={opt.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
                name={opt.key}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ---------- Stock Price Chart ----------

const TIME_RANGES = [
  { key: '1M', months: 1 },
  { key: '3M', months: 3 },
  { key: '6M', months: 6 },
  { key: '1Y', months: 12 },
  { key: '3Y', months: 36 },
  { key: '5Y', months: 60 },
  { key: 'All', months: 0 },
] as const;

interface StockPriceChartProps {
  tickerSymbol: string;
  colors: any;
}

export const StockPriceChart: React.FC<StockPriceChartProps> = ({ tickerSymbol, colors }) => {
  const [allPrices, setAllPrices] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<string>('1Y');

  useEffect(() => {
    if (!tickerSymbol) return;
    setLoading(true);
    getPriceHistory(tickerSymbol)
      .then(setAllPrices)
      .catch(() => setAllPrices([]))
      .finally(() => setLoading(false));
  }, [tickerSymbol]);

  const chartData = useMemo(() => {
    if (allPrices.length === 0) return [];
    const sorted = [...allPrices].sort((a, b) => a.date.localeCompare(b.date));
    const selected = TIME_RANGES.find(r => r.key === range)!;
    if (selected.months === 0) return sorted;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - selected.months);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return sorted.filter(p => p.date >= cutoffStr);
  }, [allPrices, range]);

  const formatLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  };

  if (loading) {
    return (
      <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
        <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Stock Price</CText>
        <div className="flex justify-center py-8"><CLoader /></div>
      </div>
    );
  }

  if (allPrices.length === 0) return null;

  const minPrice = Math.min(...chartData.map(p => p.close));
  const maxPrice = Math.max(...chartData.map(p => p.close));
  const priceChange = chartData.length >= 2 ? chartData[chartData.length - 1].close - chartData[0].close : 0;
  const priceChangePct = chartData.length >= 2 && chartData[0].close > 0
    ? ((priceChange / chartData[0].close) * 100) : 0;
  const isPositive = priceChange >= 0;
  const lineColor = isPositive ? '#22c55e' : '#ef4444';

  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <CText type="M14" color={colors.grayScale500}>Stock Price</CText>
          <span style={{ color: lineColor, fontSize: 13, fontWeight: 600 }}>
            {isPositive ? '+' : ''}{priceChangePct.toFixed(1)}%
          </span>
        </div>
        <div className="flex gap-1">
          {TIME_RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                backgroundColor: range === r.key ? colors.primary : 'transparent',
                color: range === r.key ? '#fff' : colors.grayScale500,
              }}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      {/* Price + Volume chart */}
      <div style={{ height: 280, width: '100%' }}>
        <ResponsiveContainer>
          <ComposedChart data={chartData}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={lineColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatLabel}
              tick={{ fontSize: 10, fill: colors.grayScale400 }}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="price"
              domain={[minPrice * 0.95, maxPrice * 1.05]}
              tick={{ fontSize: 10, fill: colors.grayScale400 }}
              width={55}
              tickFormatter={(v: number) => `₹${v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toFixed(0)}`}
            />
            <YAxis
              yAxisId="volume"
              orientation="right"
              tick={{ fontSize: 10, fill: colors.grayScale400 }}
              width={45}
              tickFormatter={(v: number) =>
                v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M`
                : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K`
                : String(v)
              }
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value: number, name: string) => {
                if (name === 'close') return [`₹${value.toLocaleString()}`, 'Price'];
                return [value.toLocaleString(), 'Volume'];
              }}
              labelFormatter={formatLabel}
            />
            <Bar
              yAxisId="volume"
              dataKey="volume"
              fill={colors.primary + '40'}
              radius={[2, 2, 0, 0]}
              name="volume"
            />
            <Area
              yAxisId="price"
              type="monotone"
              dataKey="close"
              stroke={lineColor}
              strokeWidth={2}
              fill="url(#priceGradient)"
              dot={false}
              activeDot={{ r: 3, fill: lineColor }}
              name="close"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
